import { isMultiSelectFilter, isRangeFilter, isBucketFilter, isLinkFilter, isNullOrUndefined, } from '@as-react/common-utils';
import { FilterActionTypes } from '../../../enums/ActionEnums';
/**
 * Compare two arrays of objects and return items that exist in both arrays (duplicates)
 */
const compareArraysOfObjectsAndReturnDuplicates = (arrayA, arrayB, keyToCompare) => {
    if (isNullOrUndefined(arrayA) || isNullOrUndefined(arrayB) || isNullOrUndefined(keyToCompare)) {
        return [];
    }
    const keys = arrayA.map(obj => obj[keyToCompare]);
    return arrayB.filter(obj => keys.indexOf(obj[keyToCompare]) > -1);
};
/**
 * Compare two arrays of objects and return items that exist only in arrayB (uniques)
 */
const compareArraysOfObjectsAndReturnUniques = (arrayA, arrayB, keyToCompare) => {
    if (isNullOrUndefined(arrayA) || isNullOrUndefined(arrayB) || isNullOrUndefined(keyToCompare)) {
        return [];
    }
    const keys = arrayA.map(obj => obj[keyToCompare]);
    return arrayB.filter(obj => keys.indexOf(obj[keyToCompare]) === -1);
};
/**
 * Check if a filter exists in the active filters
 */
const checkIfFilterOnExists = (currentActiveFilters, filterOn) => !isNullOrUndefined(currentActiveFilters) && !isNullOrUndefined(currentActiveFilters[filterOn]);
/**
 * Check if a link filter should be removed (when clicking the same filter value)
 */
const shouldRemoveLinkFilter = (currentActiveFilters, setFilterObject) => {
    var _a, _b, _c;
    const activeFilter = currentActiveFilters[setFilterObject.filterOn];
    const activeFilterKey = (_b = (_a = activeFilter === null || activeFilter === void 0 ? void 0 : activeFilter.values) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.filterKey;
    const incomingFilterKey = (_c = setFilterObject.filteredItems[0]) === null || _c === void 0 ? void 0 : _c.filterKey;
    return activeFilterKey === incomingFilterKey;
};
/**
 * Process a multiselect filter update - handles the toggle logic
 * When a filter value is clicked:
 * - If not in active filters: add it
 * - If already in active filters: remove it (toggle off)
 */
const processMultiSelectFilter = (setFilterObject, currentActiveFilters) => {
    let filteredItems = [...setFilterObject.filteredItems];
    // If multiUpdate flag is set, only keep active items
    if (setFilterObject.multiUpdate) {
        filteredItems = filteredItems.filter((item) => item.isActive);
    }
    else if (checkIfFilterOnExists(currentActiveFilters, setFilterObject.filterOn)) {
        // Toggle logic: remove duplicates (clicked items that were already active)
        const activeFilter = currentActiveFilters[setFilterObject.filterOn];
        const currentFilterKeys = (activeFilter === null || activeFilter === void 0 ? void 0 : activeFilter.values) || [];
        const existingFilterKeys = compareArraysOfObjectsAndReturnDuplicates(currentFilterKeys, filteredItems, 'filterKey');
        // New filtered items = existing items that weren't clicked + new items that weren't already active
        filteredItems = [
            ...compareArraysOfObjectsAndReturnUniques(existingFilterKeys, currentFilterKeys, 'filterKey'),
            ...compareArraysOfObjectsAndReturnUniques(existingFilterKeys, filteredItems, 'filterKey'),
        ];
    }
    return {
        filteredItems,
        shouldRemove: filteredItems.length === 0,
    };
};
/**
 * Process a range filter update
 */
const processRangeFilter = (setFilterObject, availableFilters) => {
    var _a, _b, _c, _d;
    // If reset flag is set, remove the filter
    if (setFilterObject.isReset) {
        return {
            filteredItems: [],
            shouldRemove: true,
        };
    }
    // Check if range is at boundary values - if so, remove the filter
    const filter = availableFilters.find(f => f.type === setFilterObject.filterType && f.filterOn === setFilterObject.filterOn);
    if (filter) {
        const rangeMin = Number((_a = setFilterObject.filteredItems[0]) === null || _a === void 0 ? void 0 : _a.filterKey);
        const rangeMax = Number((_b = setFilterObject.filteredItems[1]) === null || _b === void 0 ? void 0 : _b.filterKey);
        const boundaryMin = (_c = filter.rangeMin) !== null && _c !== void 0 ? _c : 0;
        const boundaryMax = (_d = filter.rangeMax) !== null && _d !== void 0 ? _d : 0;
        if (Math.ceil(rangeMax) === boundaryMax && Math.floor(rangeMin) === boundaryMin) {
            return {
                filteredItems: [],
                shouldRemove: true,
            };
        }
    }
    return {
        filteredItems: setFilterObject.filteredItems,
        shouldRemove: false,
    };
};
/**
 * Process a bucket filter update - same toggle logic as multiselect
 */
const processBucketFilter = (setFilterObject, currentActiveFilters) => {
    // Bucket filters use the same toggle logic as multiselect
    return processMultiSelectFilter(setFilterObject, currentActiveFilters);
};
/**
 * Process a link filter update - toggles off if clicking the same value
 */
const processLinkFilter = (setFilterObject, currentActiveFilters) => {
    // If the filter exists and has the same value, remove it (toggle off)
    if (checkIfFilterOnExists(currentActiveFilters, setFilterObject.filterOn) &&
        shouldRemoveLinkFilter(currentActiveFilters, setFilterObject)) {
        return {
            filteredItems: [],
            shouldRemove: true,
        };
    }
    return {
        filteredItems: setFilterObject.filteredItems,
        shouldRemove: false,
    };
};
/**
 * Main function to process any filter update
 * Returns the processed filter items and whether the filter should be removed
 */
export const processFilterUpdate = (setFilterObject, currentActiveFilters, availableFilters) => {
    if (isMultiSelectFilter(setFilterObject)) {
        return processMultiSelectFilter(setFilterObject, currentActiveFilters);
    }
    if (isRangeFilter(setFilterObject)) {
        return processRangeFilter(setFilterObject, availableFilters);
    }
    if (isBucketFilter(setFilterObject)) {
        return processBucketFilter(setFilterObject, currentActiveFilters);
    }
    if (isLinkFilter(setFilterObject)) {
        return processLinkFilter(setFilterObject, currentActiveFilters);
    }
    // Default: pass through unchanged
    return {
        filteredItems: setFilterObject.filteredItems,
        shouldRemove: setFilterObject.filteredItems.length === 0,
    };
};
/**
 * Updates the range filter with boundary values if needed
 */
export const updateRangeFilterWithBoundaries = (filter) => {
    if (filter.type === FilterActionTypes.RANGE_FILTER) {
        return updateAdditionalBoundaryDataIfNeeded(filter, filter);
    }
    return filter;
};
/**
 * Appends boundaryMin and boundaryMax to the filter if they are not already set, based on the rangeMin and rangeMax values.
 */
export const updateAdditionalBoundaryDataIfNeeded = (destination, source) => {
    var _a, _b, _c, _d;
    // @TODO lister refactor: this function is a bit complex and has some redundant checks - we should consider simplifying it when we work on the lister filters in the future
    const boundaries = {
        // @ts-expect-error - boundaryMin can be number or undefined, but we want to ensure it's set to a number (using rangeMin as fallback) for the Filter component
        boundaryMin: destination.boundaryMin,
        // @ts-expect-error - boundaryMin can be number or undefined, but we want to ensure it's set to a number (using rangeMin as fallback) for the Filter component
        boundaryMax: destination.boundaryMax,
    };
    // @ts-expect-error - boundaryMin can be number or undefined, but we want to ensure it's set to a number (using rangeMin as fallback) for the Filter component
    if (destination.boundaryMax <= source.rangeMax) {
        boundaries.boundaryMax = Math.ceil((_a = source.rangeMax) !== null && _a !== void 0 ? _a : 0);
    }
    // @ts-expect-error - boundaryMin can be number or undefined, but we want to ensure it's set to a number (using rangeMin as fallback) for the Filter component
    if (destination.boundaryMin >= source.rangeMin) {
        boundaries.boundaryMin = Math.floor((_b = source.rangeMin) !== null && _b !== void 0 ? _b : 0);
    }
    // @ts-expect-error - boundaryMin can be number or undefined, but we want to ensure it's set to a number (using rangeMin as fallback) for the Filter component
    if (!!destination.boundaryMin || destination.boundaryMin === 0) {
        return Object.assign(Object.assign({}, destination), boundaries);
    }
    return Object.assign(Object.assign({}, destination), { boundaryMin: 
        // @ts-expect-error - boundaryMin can be number or undefined, but we want to ensure it's set to a number (using rangeMin as fallback) for the Filter component
        !!source.boundaryMin || source.boundaryMin === 0 ? source.boundaryMin : Math.floor((_c = source.rangeMin) !== null && _c !== void 0 ? _c : 0), boundaryMax: 
        // @ts-expect-error - boundaryMin can be number or undefined, but we want to ensure it's set to a number (using rangeMin as fallback) for the Filter component
        !!source.boundaryMax || source.boundaryMax === 0 ? source.boundaryMax : Math.ceil((_d = source.rangeMax) !== null && _d !== void 0 ? _d : 0) });
};
